'use client';

import { faBars, faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useModalManager from '@hooks/useModalManager';
import { Button, Spinner, StyledInput } from '@ui';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { AppState } from 'redux/store';
import { Login } from '../../auth/Login';
import { Register } from '../../auth/Register';
import { UserDropdown } from '../../Dashboard';
import FavoriteClubs from '../../Dashboard/FavoriteClubs';
import GlobalOverlay from '../../GlobalOverlay';
import { Icon } from '../../Icon';

interface NavbarSharedProps {
    pathname: string;
    onSearch: (searchTerm: string) => void;
}

const NAV_LINKS = [
    { label: 'Funktionen', href: '/#start' },
    { label: 'KI & CMS', href: '/#ai' },
    { label: 'Module', href: '/#modules' },
    { label: 'Preise', href: '/#preise' }
];

export const NavbarShared = ({ pathname, onSearch }: NavbarSharedProps) => {
    const { isAuthenticated, isLoading: isLoadingAuth } = useSelector((state: AppState) => state.auth);
    const { t } = useTranslation('common');
    const { addModal } = useModalManager();
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    useEffect(() => {
        setIsMenuOpen(false);
    }, [pathname]);

    useEffect(() => {
        document.body.style.overflow = isMenuOpen ? 'hidden' : '';
        return () => {
            document.body.style.overflow = '';
        };
    }, [isMenuOpen]);

    const renderLogo = useMemo(() => {
        const currentPath = pathname || '';

        if (currentPath === '/academy')
            return (
                <div
                    style={{
                        cursor: 'pointer',
                        minWidth: '170px',
                        maxWidth: '170px',
                        flex: '1 1 170px'
                    }}
                >
                    <Image
                        alt="Akademie Logo"
                        src="/images/academy_logo.png"
                        width={6000}
                        height={1000}
                        layout="responsive"
                        objectFit="contain"
                    />
                </div>
            );

        if (currentPath.includes('ambassador'))
            return (
                <div
                    style={{
                        cursor: 'pointer',
                        minWidth: '170px',
                        maxWidth: '170px',
                        flex: '1 1 170px'
                    }}
                >
                    <Image
                        alt="Botschafter Logo"
                        src="/images/ambassador/ambassador_logo.png"
                        width={6000}
                        height={1000}
                        layout="responsive"
                        objectFit="contain"
                    />
                </div>
            );

        if (currentPath.startsWith('/developer'))
            return (
                <div
                    style={{
                        cursor: 'pointer',
                        minWidth: '170px',
                        maxWidth: '170px',
                        flex: '1 1 170px'
                    }}
                >
                    {/* Replace with <Image src="/images/developer_logo.png" ...> once the file is added */}
                    <Image
                        alt="Developer Logo"
                        src="/images/headless_logo.png"
                        width={6000}
                        height={1000}
                        layout="responsive"
                        objectFit="contain"
                        onError={(e) => {
                            (e.currentTarget as HTMLImageElement).style.display = 'none';
                        }}
                    />
                </div>
            );

        return (
            <Icon
                font={true}
                width={40}
                style={{
                    cursor: 'pointer',
                    minWidth: '170px',
                    maxWidth: '170px',
                    flex: '1 1 170px'
                }}
            />
        );
    }, [pathname]);

    const openRegister = () =>
        addModal({
            content: <Register loginCallback={openLogin} affiliate={null} />,
            title: t('auth:Registrieren'),
            show: true
        });

    const openLogin = () => {
        addModal({
            content: <Login registerCallback={openRegister} />,
            title: t('auth:Login'),
            show: true,
            size: 60
        });
    };

    const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const target = e.currentTarget as typeof e.currentTarget & {
            search: { value: string };
        };
        onSearch(target.search.value);
    };

    return (
        <>
            {/* Height Placeholder */}
            <div className="tw-h-16 tw-w-full tw-bg-transparent" />

            {/* Navbar */}
            <nav className="tw-fixed tw-left-0 tw-right-0 tw-top-0 tw-z-50 tw-border-b tw-border-solid tw-border-accents-2 tw-bg-white/80 tw-px-6 tw-py-3 tw-backdrop-blur-xl">
                <div className="tw-container tw-flex tw-items-center tw-justify-between tw-gap-4">
                    <span className="tw-hidden md:tw-inline-block">
                        <Link title="Homepage" href="/">
                            {renderLogo}
                        </Link>
                    </span>
                    <span className="tw-inline-block md:tw-hidden">
                        <Link href="/">
                            <Icon
                                font={false}
                                width={40}
                                style={{
                                    cursor: 'pointer',
                                    minWidth: '40px',
                                    maxWidth: '40px',
                                    flex: '1 1 40px'
                                }}
                            />
                        </Link>
                    </span>
                    <form
                        className="tw-hidden tw-flex-1 tw-justify-center md:tw-flex md:tw-min-w-[500px]"
                        onSubmit={handleSearchSubmit}
                    >
                        <StyledInput
                            className="tw-max-w-[500px]"
                            fullWidth
                            name="search"
                            contentRightStyling={false}
                            contentRight={
                                <Button type="submit" css={{ px: '$8', ml: '$6' }} auto flat color="success">
                                    <FontAwesomeIcon icon={faSearch} />
                                </Button>
                            }
                            placeholder={t('Suche')}
                        />
                    </form>
                    <div className="tw-flex tw-items-center tw-gap-2">
                        {/* Mobile search */}
                        <Button.Link href="/search" size="md" className="tw-block md:tw-hidden" flat>
                            <FontAwesomeIcon icon={faSearch} />
                        </Button.Link>

                        {isLoadingAuth && <Spinner />}

                        {/* Desktop authenticated */}
                        {!isLoadingAuth && isAuthenticated && (
                            <span className="tw-hidden tw-items-center tw-gap-4 md:tw-flex">
                                <FavoriteClubs />
                                <UserDropdown placement="bottom-end" />
                            </span>
                        )}

                        {/* Desktop unauthenticated */}
                        {!isLoadingAuth && !isAuthenticated && (
                            <span className="tw-hidden tw-gap-4 md:tw-flex">
                                <Button size="md" flat color="light" onPress={openLogin}>
                                    <Trans ns="auth">Einloggen</Trans>
                                </Button>
                                <Button size="md" color="success" onPress={openRegister}>
                                    <Trans ns="auth">Registrieren</Trans>
                                </Button>
                            </span>
                        )}

                        {/* Mobile hamburger — always visible on small screens */}
                        <Button
                            size="md"
                            flat
                            className="tw-block md:tw-hidden"
                            onPress={() => setIsMenuOpen((prev) => !prev)}
                            aria-label={isMenuOpen ? 'Menü schließen' : 'Menü öffnen'}
                        >
                            <FontAwesomeIcon icon={isMenuOpen ? faTimes : faBars} />
                        </Button>
                    </div>
                </div>
            </nav>

            {/* Mobile full-screen overlay menu */}
            {isMenuOpen && (
                <div className="tw-fixed tw-inset-0 tw-z-[60] tw-flex tw-flex-col tw-overflow-y-auto tw-bg-white md:tw-hidden">
                    {/* Menu header */}
                    <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-solid tw-border-accents-2 tw-px-6 tw-py-4">
                        <Link href="/" onClick={() => setIsMenuOpen(false)}>
                            <Icon
                                font={false}
                                width={40}
                                style={{ cursor: 'pointer', minWidth: '40px', maxWidth: '40px' }}
                            />
                        </Link>
                        <button
                            onClick={() => setIsMenuOpen(false)}
                            className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-bg-accents-1 tw-text-accents-7 hover:tw-bg-accents-2"
                            aria-label="Menü schließen"
                        >
                            <FontAwesomeIcon icon={faTimes} className="tw-text-lg" />
                        </button>
                    </div>

                    {/* Navigation links */}
                    <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-1 tw-px-4 tw-py-6">
                        {NAV_LINKS.map(({ label, href }) => (
                            <Link
                                key={href}
                                href={href}
                                onClick={() => setIsMenuOpen(false)}
                                className="tw-block tw-rounded-xl tw-px-4 tw-py-4 tw-text-xl tw-font-semibold tw-text-accents-9 tw-no-underline tw-transition-colors hover:tw-bg-successLight/30 hover:tw-text-success"
                            >
                                {label}
                            </Link>
                        ))}
                    </div>

                    {/* Auth section */}
                    <div className="tw-border-t tw-border-solid tw-border-accents-2 tw-px-6 tw-py-6">
                        {!isLoadingAuth && !isAuthenticated && (
                            <div className="tw-flex tw-flex-col tw-gap-3">
                                <Button
                                    block
                                    size="lg"
                                    color="success"
                                    onPress={() => {
                                        setIsMenuOpen(false);
                                        openRegister();
                                    }}
                                >
                                    <Trans ns="auth">Registrieren</Trans>
                                </Button>
                                <Button
                                    block
                                    size="lg"
                                    flat
                                    color="light"
                                    onPress={() => {
                                        setIsMenuOpen(false);
                                        openLogin();
                                    }}
                                >
                                    <Trans ns="auth">Einloggen</Trans>
                                </Button>
                            </div>
                        )}
                        {!isLoadingAuth && isAuthenticated && (
                            <div className="tw-flex tw-items-center tw-gap-4">
                                <FavoriteClubs />
                                <UserDropdown placement="top-end" />
                            </div>
                        )}
                    </div>
                </div>
            )}

            <GlobalOverlay />
        </>
    );
};
